import React from 'react';
import { HEALTH_BAND_BG, HEALTH_BAND_RING } from '../lib/health';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';

/** Compact circular health-score chip — no labels, just the number + ring.
 *
 * Score 0..100 con ring riempito proporzionalmente (score/100 della
 * circonferenza). `?` quando lo store non ha dati sufficienti.
 *
 * Sizes: `sm` (38px), `md` (52px), `lg` (72px).
 *
 * Tooltip on hover spiega in modo sintetico come si calcola lo score
 * (KPI primari pesano doppio, soglie band, esclusioni). Include sempre
 * un proprio `TooltipProvider` così funziona ovunque venga montato il
 * badge senza richiedere al parent di provvedere il provider.
 */
export default function HealthBadge({ health, size = 'sm', label }) {
  const px = size === 'lg' ? 72 : size === 'md' ? 52 : 38;
  const stroke = size === 'lg' ? 6 : 4;
  const score = health?.score;
  const band = health?.band || 'neutral';
  const counts = health?.counts || { green: 0, yellow: 0, red: 0 };
  const sample = health?.sample || 0;
  const noData = score == null;

  const r = (px - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = noData ? 0 : (score / 100) * c;

  const bandLabel = noData ? 'Dati insufficienti'
    : band === 'green' ? 'In target'
    : band === 'yellow' ? 'Attenzione'
    : band === 'red' ? 'Sotto target'
    : '—';

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className="inline-flex items-center gap-2 cursor-help"
            data-testid="health-badge"
          >
            <div className="relative flex-shrink-0" style={{ width: px, height: px }}>
              <svg width={px} height={px}>
                <circle cx={px / 2} cy={px / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
                {!noData && (
                  <circle
                    cx={px / 2} cy={px / 2} r={r}
                    fill="none" strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={`${dash} ${c}`}
                    className={`${HEALTH_BAND_RING[band]} transition-all`}
                    transform={`rotate(-90 ${px / 2} ${px / 2})`}
                  />
                )}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className={`tabular-nums font-bold ${size === 'lg' ? 'text-lg' : size === 'md' ? 'text-sm' : 'text-[11px]'} ${
                    noData ? 'text-slate-400' : ''
                  }`}
                >
                  {noData ? '—' : score}
                </span>
              </div>
            </div>
            {label && (
              <div className="min-w-0">
                <span
                  className={`inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-semibold border ${HEALTH_BAND_BG[noData ? 'neutral' : band]}`}
                >
                  {label}
                </span>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[300px] p-3 text-xs leading-relaxed">
          <p className="font-semibold text-sm mb-1.5">
            Health Score {noData ? '' : `· ${score}/100`} <span className="font-normal text-slate-300">· {bandLabel}</span>
          </p>
          {!noData && sample > 0 && (
            <p className="mb-2 text-slate-200">
              <span className="text-emerald-300">{counts.green} verdi</span>
              {' · '}
              <span className="text-amber-300">{counts.yellow} gialli</span>
              {' · '}
              <span className="text-red-300">{counts.red} rossi</span>
              {' '}su {sample} KPI
            </p>
          )}
          <p className="text-slate-200">
            Media pesata dei semafori budget dei KPI. I <b>KPI principali</b> (vendite, ticket medio,
            ricavo orario, occupazione) pesano il doppio rispetto a quelli secondari.
          </p>
          <ul className="mt-2 space-y-0.5 text-slate-200">
            <li><b className="text-emerald-300">Verde</b> · score &gt; 75 — o 70-75 con più verdi che rossi</li>
            <li><b className="text-amber-300">Giallo</b> · score 60-75 (zona attenzione)</li>
            <li><b className="text-red-300">Rosso</b> · score &lt; 60</li>
          </ul>
          <p className="mt-2 text-[10px] text-slate-400">
            Esclusi dal calcolo: Ore Disponibili e Clienti Abituali.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
